import React, { useEffect, useRef, useState, useCallback } from 'react'; // useRef - DOM access and keeping values across renders
import MobileNav from '../components/MobileNav'
import logo from '../assets/images/wordb_logo.svg'
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaArrowRight } from "react-icons/fa";
import { io } from 'socket.io-client';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import { LuAlarmClock } from "react-icons/lu";
//import { FaCheck } from "react-icons/fa6";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
//import Keyboard from '../components/Keyboard';
import BurstAnimation from '../components/BurstAnimation';
import { FaRegClipboard } from "react-icons/fa";
import Footer from '../components/Footer'; 
import Feedback from '../components/Feedback';
import Query from '../components/Query';
import { IoClose } from "react-icons/io5";
import { API, resolveBackendPath } from '../config';



const GameRoom = () => {
  
  const defAvatar = '/assets/images/def_avatar.jpg';

  function resolveAvatar(src) {
    return resolveBackendPath(src, defAvatar);
  }

  const { roomId } = useParams(); // Get the room ID from the route
  const navigate = useNavigate();
  const [showBurst, setShowBurst] = useState(false);
  const [bursting, setBursting] = useState(false);
  const prevGameStateRef = useRef(null);


  // States for the game
  const [gameState, setGameState] = useState(null); // Holds the game state from the backend
  const [word, setWord] = useState(''); // Holds the inputted word
  const [players, setPlayers] = useState([]); // Stores players in the room
  const [socket, setSocket] = useState(null); // Socket.IO instance
  const [timer, setTimer] = useState(15);
  const [currentUser, setCurrentUser] = useState(null); // Store current user
  const [isGameOver, setIsGameOver] = useState(false);
  const [isValidatingWord, setIsValidatingWord] = useState(false);
  const [isStartingGame, setIsStartingGame] = useState(false);

  const [isFeedbackOpen, setFeedbackOpen] = useState(false);
  const [isQueryOpen, setQueryOpen] = useState(false);
  const toggleFeedback = () => setFeedbackOpen(open => !open);
  const toggleQuery = () => setQueryOpen(open => !open);

  const inputRef = useRef(null);



  const copyRoomIdToClipboard = () => {
    // Now `roomId` is the actual string from useParams()
   // console.log("→ raw roomId:", roomId, "| typeof:", typeof roomId);

    // Feature‐detect the Clipboard API
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      try {
        navigator.clipboard
          .writeText(String(roomId))
          .then(() => {
            toast.success("Game ID copied to clipboard!");
          })
      } catch (err) {
        // Fails silently on browsers that reject or block it
        console.warn('Clipboard write failed, but we’re okay:', err);
      }
      return;
    };

  }

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const userId = decoded.id.toString(); // Extract the user's ID from the token

        //console.log('PLAYERS:', players); // Debugging line
        if (players.length > 0) {
          const user = players.find(player => String(player.id) === userId);
          setCurrentUser(user || null);// Debugging line 
        }
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }
  }, [players]); // Re-run this effect whenever players change
  // Runs whenever players list updates

  // Fetch initial game state and set up the socket connection
  useEffect(() => {
    const fetchGameState = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await axios.get(`${API}/api/game/room/${roomId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setGameState(response.data.gameState);
        setPlayers(response.data.players);
      } catch (error) {
        console.error('Error fetching game state:', error.response?.data || error.message);
        toast.error('Failed to fetch game data. Redirecting...');
        setTimeout(() => navigate('/home'), 3000);
      }
    };

    fetchGameState();

    // Initialize the Socket.IO connection (protected default namespace '/')
    const newSocket = io(API, {
      auth: { token: localStorage.getItem('authToken') },
      withCredentials: true,
    });

    // Store the socket instance
    setSocket(newSocket);

    newSocket.on('connect', () => {
      newSocket.emit('joinRoom', { roomId }); //Removed userId
    });

    // Cleanup the socket connection on component unmount
    return () => {
      newSocket.disconnect();
    };
  }, [roomId, navigate]);

  // Listen to socket events after socket is initialized
  useEffect(() => {
    if (!socket) return; // Ensure socket is initialized before setting up listeners


    // Listen for player updates
    socket.on('playerJoined', (updatedPlayers) => {
      setPlayers(updatedPlayers);
    });

    // Listen for gameStarted event
    socket.on('gameStarted', ({ gameState, timer }) => {
      setGameState(gameState);
      setTimer(timer); // Sync the timer
      setIsGameOver(false); 
      toast.success('Game started!');
    });


    // (Optional) keep support for your older event name
    socket.on('updateTimer', ({ time }) => {
      setTimer(time);
    });


    socket.on('opponentDisconnected', ({ message }) => {
      setIsGameOver(true);   
      toast.info(message, {
        autoClose: 3000,         // show for 3s
        onClose: () => navigate('/'),  // then navigate
      });
    });


    socket.on('turnUpdate', ({ gameState: newState, timer }) => {

      //console.log("PREV", prevGameStateRef.current?.currentTurn);
      //console.log("CURRENT USER", currentUser?.username);
      const wasUserTurn = prevGameStateRef.current?.currentTurn === currentUser?.username;

      if (wasUserTurn || !prevGameStateRef.current) {
        setShowBurst(true);
        setBursting(true);

        // Auto-hide burst and reset animation flag after ~1s
        setTimeout(() => {
          setShowBurst(false);
          setBursting(false);
        }, 1000);
      } else {
        setBursting(true);

        // Auto-hide burst and reset animation flag after ~1s
        setTimeout(() => {
          setBursting(false);
        }, 1000);
      }



      prevGameStateRef.current = newState; // update the ref
      setGameState(newState);

      //console.log("GAME STATE", gameState)
      setTimer(timer);
    });


    // Listen for gameEnded event
    socket.on('gameEnded', ({ message }) => {
      setIsGameOver(true);   
      toast.info(message, {
        autoClose: 3000,               // give them 3s to read it
        onClose: () => navigate('/leaderboard'),
      });
    });

    return () => {
      socket.off('updateTimer');
      socket.off('playerJoined');
      socket.off('turnUpdate');
      socket.off('gameStarted');
      socket.off('gameEnded');
      socket.off('opponentDisconnected');
    };
  }, [socket, navigate, gameState, currentUser]);

  // const handleKeyPress = (e) => {
  //   if (e.key === 'Enter') {
  //     handleSubmit(e); // Trigger word submission
  //   }
  // };





  // Trigger the "start" endpoint
  const handleStartGame = async () => {
    //console.log("GAME STATE", gameState)
    setIsStartingGame(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(
        `${API}/api/game/start`,
        { roomId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setGameState(response.data.gameState); // Update the game state after starting
    } catch (error) {
      console.error('Error starting game:', error.response?.data || error.message);
      toast.error('Failed to start the game.');
    } finally {
      setIsStartingGame(false);
    }
  };

  // Submit a word
  const handleSubmit = useCallback(async (e) => {
    if (e?.preventDefault) e.preventDefault();

    if (isGameOver || timer <= 0) {
      toast.info('Time is up.');
      return;
    }

    if (!word.trim()) {
      toast.warn('Please enter a word.');
      return;
    }

    const token = localStorage.getItem('authToken');

    setIsValidatingWord(true);

    try {
      await axios.post(
        `${API}/api/game/submit-word`,
        { roomId, word: word.trim().toLowerCase() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWord('');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit the word.');
    } finally {
      setIsValidatingWord(false);
    }
  }, [
    isGameOver,
    timer,
    word,
    roomId,
  ]);

  const handleLetterInput = useCallback((key) => {
    // ignore when not user's turn or game over
    if (isGameOver || timer <= 0 || gameState?.currentTurn !== currentUser?.username) return;

    if (key === 'BACKSPACE' || key === 'Backspace') {
      setWord((prev) => (prev ? prev.slice(0, -1) : prev));
      return;
    }

    if (key === 'Enter') {
      handleSubmit();
      return;
    }

    if (/^[a-zA-Z]$/.test(key)) {
      setWord((prev) => prev + key.toUpperCase());
    }
  }, [isGameOver, timer, gameState?.currentTurn, currentUser?.username, handleSubmit]);


useEffect(() => {
  const hotkeysHandler = (e) => {
    const el = document.activeElement;
    if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
    if (e.ctrlKey || e.metaKey || e.altKey) return;

    handleLetterInput(e.key);
  };

  window.addEventListener('keydown', hotkeysHandler);
  return () => window.removeEventListener('keydown', hotkeysHandler);
}, [handleLetterInput]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);






  // Show loading if gameState is null
  if (!gameState) {
    return (
      <section className='lobby2'>
        <ToastContainer position="top-right" autoClose={3000} />

        {isFeedbackOpen && <Feedback onClose={toggleFeedback} />}
        {isQueryOpen && <Query onClose={toggleQuery} />}

        <Link to="/lobby1" className='left-icon-link'>
          <IoClose className='icon' />
        </Link>

        <MobileNav />

        <div className="logo-div">
            <img src={logo} alt="" />
        </div>

        <div className="lobby2-content">
          <p id="gameID">
            Game ID: &nbsp;
            <span
              onClick={copyRoomIdToClipboard}
              role="button"
              tabIndex={0}
              style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
            >
              <FaRegClipboard className="icon" />&nbsp;{roomId}
            </span>
          </p>

          <div className="inner-content">
            <p>Players in the Game</p>

            <div className="players-div">
              {players.map((player) => (
                <div className="player" key={player.id}>
                  <div className="pic">
                    <img
                      src={resolveAvatar(player.avatar)}
                      onError={(e) => { e.currentTarget.src = defAvatar; }}
                      alt={player.username}
                    />
                  </div>
                  <p>{player.username}</p>
                </div>
              ))}
            </div>
          </div>

          {players.length < 2 ? (
            <p className='wait-text'>Waiting for other player to join...</p>
          ) : (
            <button className="btn join-btn start-game-btn" onClick={handleStartGame}>
              <span>{ isStartingGame ? 'Starting game...' : 'Start Game'}</span>
              <FaArrowRight className="icon" />
            </button>
          )}
        </div>

        <Footer onOpenFeedback={toggleFeedback} onOpenQuery={toggleQuery}/>
      </section>
    );
  }

  const canType =
    !isGameOver &&
    timer > 0 &&
    gameState?.currentTurn === currentUser?.username;


  return (
    <>

      <section className="game-play">
        <ToastContainer position="top-right" autoClose={3000} />

        <div className="game-nav">
          <IoClose
            className="forfeit-btn"
            onClick={() =>
              socket.emit('forfeitGame', {
                roomId,
                username: currentUser?.username,
              })
            }
          />
        </div>

        <div className="game-container">
          <div className="board">
            <div className="timer">
              <LuAlarmClock className="icon" />
              <p className="time">{timer}&nbsp;s</p>
            </div>

            <div className="score-div">
              <p className="score-label">Your Score</p>
              <p className="score">{gameState.scores[currentUser?.username] || 0}</p>
            </div>
          </div>

          <div className="current-word burst-wrapper">
            {showBurst && <BurstAnimation show={showBurst} />}
            <p className={`word ${bursting ? 'bursting' : ''}`}>
              {gameState.previousWord || "Waiting..."}
            </p>
          </div>


          <form onSubmit={(e) => e.preventDefault()}>

            <div 
              className="custom-input-box native-wrap"
              onClick={() => inputRef.current?.focus()}
            >
              {word ? (
                      <>
                        <span className="typed-text">{word}</span>
                        <span className="cursor" />
                      </>
              ) : (

                <span className="placeholder">
                  {isGameOver
                    ? 'Game over'
                    : gameState.currentTurn === currentUser?.username
                      ? 'Start typing your word'
                      : `${gameState.currentTurn} typing...`}
                </span>
              )}

              <input
                autoFocus
                ref={inputRef}
                className="native-input"
                type="text"
                inputMode="text"
                enterKeyHint="done"

                autoCorrect="off"
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}

                // IMPORTANT: keep it uncontrolled so Android fires input events reliably
                defaultValue=""

                onPaste={(e) => e.preventDefault()}

                onKeyDown={(e) => {
                  if (!canType) return;

                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSubmit();
                  }

                  // Backspace fallback (some keyboards won’t emit delete via input)
                  if (e.key === "Backspace") {
                    e.preventDefault();
                    handleLetterInput("Backspace");
                  }
                }}

                onInput={(e) => {
                  if (!canType) {
                    // keep field empty even if they type during opponent turn
                    e.currentTarget.value = "";
                    return;
                  }

                  const val = e.currentTarget.value || "";

                  // Accept exactly 1 letter only
                  if (val.length === 1 && /^[a-zA-Z]$/.test(val)) {
                    handleLetterInput(val);
                  }

                  // Always clear so suggestion/word insert can’t accumulate
                  e.currentTarget.value = "";
                }}
              />


            </div>


            <button type="button" className="submit-word-btn btn" onClick={handleSubmit} disabled={isGameOver || gameState.currentTurn !== currentUser?.username}>
              <p className="btn-text">{ isValidatingWord ? 'Validating...' : 'Submit'}</p>
              {/* <FaCheck className="icon" /> */}
            </button>


          </form>
        </div>

        <div className="tap-text-div" onClick={() => inputRef.current?.focus()}>
              <p className='tap-text'>Tap here to bring up your keyboard</p>
        </div>



        {/* <Keyboard 
          disabled={isGameOver || gameState.currentTurn !== currentUser?.username}
          onKeyPress={handleLetterInput}
        /> */}
      </section> 


    </>

  );
};

export default GameRoom;
